import {
  type Type,
  Types,
  Plugins,
  Expressions,
  Variable,
  type ExpressionPath,
  matchExpression,
  Component,
  type CheckerState,
} from '@packages/systems/wfdl';
import {
  identifier,
  namesEqual,
  QualifiedMap,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  EBoolean,
  ECall,
  EEnum,
  EElement,
  EHole,
  ELiteral,
  EList,
  ERecord,
  EUnion,
  EVariable,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {generateGuid} from '@packages/utilities/guid';
import {buildNewCondition} from '@packages/systems/plugins-utils/Conditions';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {Every, Some} from '@packages/systems/plugins-utils/Boolean';
import {constantFalse, identity} from '@packages/utilities/fp/utils';
import {hasAvailableOperands} from './hasAvailableOperands';

export {hasAvailableOperands} from './hasAvailableOperands';

const CONDITIONAL_NAME = qualify(
  Plugins.name('Conditionals'),
  typeName('Conditional')
);

const STYLE_VARIANT_TYPE_NAME = qualify(
  Plugins.name('Basic'),
  typeName('StyleVariant')
);
const LINK_TYPE_NAME = qualify(Plugins.name('Basic'), typeName('Link'));
const VIDEO_TYPE_NAME = qualify(Plugins.name('Embed'), typeName('Video'));
const HEADING_TAG_TYPE_NAME = qualify(
  Plugins.name('Basic'),
  typeName('HeadingTag')
);

export function buildNewConditionalExpression(type: Type) {
  const conf = getSupportedTypeConfiguration(type);

  // If the type is a boolean, set the output of the rule to True and the fallback to False
  // Otherwise, create a new conditional expression with a hole for the output and a hole for the fallback
  const output = conf ? conf.defaultOutputValue() : EHole();
  const fallback = conf ? conf.defaultFallbackValue() : EHole();

  return ECall({
    fun: EVariable(CONDITIONAL_NAME),
    arg: ERecord({
      rules: EList([buildNewConditionalRuleExpression(output)]),
      fallback,
    }),
  });
}

export function buildNewConditionalRuleExpression(outputExpr: Expression) {
  return ERecord({
    condition: ECall({
      fun: EVariable(Every),
      arg: EList([buildNewCondition()]),
    }),
    output: outputExpr,
  });
}

type SupportedTypeConfiguration = {
  defaultOutputValue: () => Expression;
  defaultFallbackValue: () => Expression;
  newGroupOutputValueMapper: (fallbackExpr: ContextualExpression) => Expression;
  simpleConditional?: {
    valueAutoAssignMapper: (expr: Expression) => Expression | null;
    hidePreviewFallbackValue: (a: Expression, b: Expression) => boolean;
  };
  keepMiniSettingsOpen?: boolean;
};

const BOOLEAN_UNION_TRUE = EUnion({tag: 'True', value: ERecord({})});
const BOOLEAN_UNION_FALSE = EUnion({tag: 'False', value: ERecord({})});

function createDefaultListOfElementValue(): Expression {
  return EList([
    EElement({
      id: generateGuid(),
      type: qualify(Plugins.name('Basic'), Component.name('String')),
      data: EText(''),
    }),
  ]);
}

const STYLE_VARIANT_BASE = ELiteral({
  name: STYLE_VARIANT_TYPE_NAME,
  value: 'base',
});

const HEADING_TAG_H1 = EEnum('h1');

const LINK_PLACEHOLDER = ELiteral({
  name: LINK_TYPE_NAME,
  value: {mode: 'external', url: '#'},
});

const VIDEO_PLACEHOLDER = ELiteral({
  name: VIDEO_TYPE_NAME,
  value: {embed: {loaded: false, type: 'video', url: '', title: ''}},
});

const IMAGE_PLACEHOLDER_ID = 'plugins/Basic/assets/placeholder.svg';
const IMAGE_PLACEHOLDER = ELiteral({
  name: qualify(Plugins.name('Asset'), typeName('Image')),
  value: {id: IMAGE_PLACEHOLDER_ID},
});

const IMAGE_TYPE_NAME = qualify(Plugins.name('Asset'), typeName('Image'));
const LIST_OF_ELEMENT_SUPPORTED_TYPE_CONFIGURATION: SupportedTypeConfiguration =
  {
    defaultOutputValue: createDefaultListOfElementValue,
    defaultFallbackValue: createDefaultListOfElementValue,
    newGroupOutputValueMapper: function listOfElementNewGroupOutputValueMapper(
      _fallbackExpr: ContextualExpression
    ) {
      return createDefaultListOfElementValue();
    },
  };

const SUPPORTED_TYPES = [
  {
    name: qualify(Plugins.name('Boolean'), typeName('Boolean')),
    defaultOutputValue: () => BOOLEAN_UNION_TRUE,
    defaultFallbackValue: () => BOOLEAN_UNION_FALSE,
    simpleConditional: {
      valueAutoAssignMapper: function booleanAutoValueMapper(expr: Expression) {
        const union = Expressions.getUnion(expr);
        if (union) {
          return union.tag === 'True'
            ? BOOLEAN_UNION_FALSE
            : BOOLEAN_UNION_TRUE;
        }
        return null;
      },
      hidePreviewFallbackValue: function hideBooleanPreviewFallbackValue(
        a: Expression,
        b: Expression
      ) {
        const aUnion = Expressions.getUnion(a);
        const bUnion = Expressions.getUnion(b);
        // If either expression is not a union, don't hide the fallback
        // In that case, either one of the expressions is a binding and we want to show the fallback
        if (!aUnion || !bUnion) {
          return false;
        }
        return aUnion.tag !== bUnion.tag;
      },
    },
    newGroupOutputValueMapper: function booleanNewGroupOutputValueMapper(
      fallbackExpr: ContextualExpression
    ) {
      const union = ContextualExpressions.getUnion(stripAnn(fallbackExpr));
      if (union) {
        return union.tag === 'True' ? BOOLEAN_UNION_FALSE : BOOLEAN_UNION_TRUE;
      }
      return BOOLEAN_UNION_TRUE;
    },
    keepMiniSettingsOpen: true,
  },
  {
    name: qualify(Plugins.name('Visibility'), typeName('VisibilityConditions')),
    defaultOutputValue: () => EList([EBoolean(true)]),
    defaultFallbackValue: () => EList([EBoolean(false)]),
    newGroupOutputValueMapper: function visibilityNewGroupOutputValueMapper(
      fallbackExpr: ContextualExpression
    ) {
      const list = ContextualExpressions.getList(stripAnn(fallbackExpr));
      const visibleExpr = list ? list[0] : null;
      const visible = visibleExpr
        ? ContextualExpressions.getBoolean(stripAnn(visibleExpr))
        : null;
      if (typeof visible === 'boolean') {
        return EList([EBoolean(!visible)]);
      }
      return EList([EBoolean(true)]);
    },
    keepMiniSettingsOpen: true,
  },
  {
    name: STYLE_VARIANT_TYPE_NAME,
    defaultOutputValue: () => STYLE_VARIANT_BASE,
    defaultFallbackValue: () => STYLE_VARIANT_BASE,
    newGroupOutputValueMapper: () => STYLE_VARIANT_BASE,
    keepMiniSettingsOpen: true,
  },
  {
    name: HEADING_TAG_TYPE_NAME,
    defaultOutputValue: () => HEADING_TAG_H1,
    defaultFallbackValue: () => HEADING_TAG_H1,
    newGroupOutputValueMapper: () => HEADING_TAG_H1,
  },
  {
    name: LINK_TYPE_NAME,
    defaultOutputValue: () => LINK_PLACEHOLDER,
    defaultFallbackValue: () => LINK_PLACEHOLDER,
    newGroupOutputValueMapper: () => LINK_PLACEHOLDER,
  },
  {
    name: VIDEO_TYPE_NAME,
    defaultOutputValue: () => VIDEO_PLACEHOLDER,
    defaultFallbackValue: () => VIDEO_PLACEHOLDER,
    newGroupOutputValueMapper: () => VIDEO_PLACEHOLDER,
  },
  {
    name: IMAGE_TYPE_NAME,
    defaultOutputValue: () => IMAGE_PLACEHOLDER,
    defaultFallbackValue: () => IMAGE_PLACEHOLDER,
    newGroupOutputValueMapper: () => IMAGE_PLACEHOLDER,
  },
  {
    name: qualify(Plugins.name('Builtin'), Component.name('Text')),
    defaultOutputValue: () => EText(''),
    defaultFallbackValue: () => EText(''),
    newGroupOutputValueMapper: function textNewGroupOutputValueMapper(
      _fallbackExpr: ContextualExpression
    ) {
      return EText('');
    },
  },
].reduce<QualifiedMap<string, SupportedTypeConfiguration>>(
  (qmap, conf) =>
    qmap.set(conf.name, {
      defaultFallbackValue: conf.defaultFallbackValue,
      defaultOutputValue: conf.defaultOutputValue,
      simpleConditional: conf.simpleConditional,
      newGroupOutputValueMapper: conf.newGroupOutputValueMapper,
      keepMiniSettingsOpen: conf.keepMiniSettingsOpen,
    }),
  QualifiedMap.empty()
);

function getSupportedTypeConfiguration(
  type: Type | null
): SupportedTypeConfiguration | null {
  if (!type) {
    return null;
  }
  if (Types.isTypeListOfElements(type)) {
    return LIST_OF_ELEMENT_SUPPORTED_TYPE_CONFIGURATION;
  }
  const decon = Types.deconstructTypeApplication(type);
  return decon ? (SUPPORTED_TYPES.get(decon.con) ?? null) : null;
}

/**
 * Checks if a type can support Conditionals
 * @param type - The type to check
 * @returns Whether the type supports Conditionals
 */
export function supportsConditionals(type: Type): boolean {
  if (Types.isTypeListOfElements(type)) {
    return true;
  }
  const decon = Types.deconstructTypeApplication(type);
  if (decon) {
    return SUPPORTED_TYPES.has(decon.con);
  }
  return false;
}

/**
 * Checks if the expression root or an expression along the path contains a conditional expression
 * by traversing the path and checking if any of the expressions are a conditional expression
 */
function pathContainsConditional(
  expr: ContextualExpression | null,
  path: ExpressionPath
): boolean {
  if (!expr) {
    return false;
  }
  if (isConditionalCE(expr)) {
    return true;
  }
  let currentExpr: ContextualExpression = expr;
  for (let i = 0; i < path.length; i++) {
    const pathPart = path[i];
    const nextExpr = pathPart
      ? ContextualExpressions.getIn(currentExpr, [pathPart])
      : null;
    if (!nextExpr) {
      return false;
    }
    if (isConditionalCE(nextExpr)) {
      return true;
    }
    currentExpr = nextExpr;
  }
  return false;
}

const isConditionalVar = Variable.equals(CONDITIONAL_NAME);

export const isConditionalReferenceCE = ContextualExpressions.matchExpression({
  Variable: isConditionalVar,
  _: constantFalse,
});

export function isConditionalCE(expr: ContextualExpression) {
  const callExpr = ContextualExpressions.getCall(stripAnn(expr));
  if (callExpr) {
    return isConditionalReferenceCE(stripAnn(callExpr.fun));
  }
  return false;
}

export const isConditionalReference = matchExpression<boolean>({
  Variable: isConditionalVar,
  _: constantFalse,
});

export const isConditional = matchExpression<boolean>({
  Call: (call) => isConditionalReference(call.fun),
  _: constantFalse,
});

export const ConditionalModes = {
  All: 'all',
  Any: 'any',
} as const;

export type ConditionalMode =
  (typeof ConditionalModes)[keyof typeof ConditionalModes];

/*
 * Helper function to detect if a conditional expression uses Boolean Every or Some.
 * If it uses Boolean Every it returns 'All', if it uses Boolean Some it returns 'Any'. Defaults to 'All'
 */
export function getConditionalMode(
  conditionExpr: ContextualExpression
): ConditionalMode {
  const callResult = ContextualExpressions.getCall(stripAnn(conditionExpr));
  if (!callResult) return ConditionalModes.All;

  const callFun = stripAll(callResult.fun);
  const variableExpr = Expressions.getVariable(callFun);
  if (!variableExpr) return ConditionalModes.All;

  if (Variable.equals(variableExpr)(Some)) {
    return ConditionalModes.Any;
  }

  return ConditionalModes.All;
}

/*
 * Helper function to create a new conditional expression with the specified mode
 */
export function createConditionalWithMode(
  conditionExpr: ContextualExpression,
  mode: ConditionalMode
): Expression {
  const callResult = ContextualExpressions.getCall(stripAnn(conditionExpr));
  if (!callResult) return stripAll(conditionExpr);

  const booleanVariable = mode === ConditionalModes.Any ? Some : Every;
  const newFun = EVariable(booleanVariable);

  return ECall({
    fun: newFun,
    arg: stripAll(callResult.arg),
  });
}

/*
 * Helper function to get the default output value for a new group.
 * If the type is Boolean and the fallback is True, it returns False.
 * If the type is Boolean and the fallback is False, it returns True.
 * If the type is Boolean and the fallback is a select expression, it returns True.
 * Otherwise it returns a hole.
 */
export function getNewGroupDefaultOutputValue(
  type: Type | null,
  fallbackExpr: ContextualExpression
): Expression {
  const conf = getSupportedTypeConfiguration(type);
  if (conf) {
    return conf.newGroupOutputValueMapper(fallbackExpr);
  }
  return EHole();
}

export function getSimpleConditionalAutoAssignedValue(
  type: Type,
  expr: Expression
) {
  const conf = getSupportedTypeConfiguration(type);
  if (conf && conf.simpleConditional) {
    return conf.simpleConditional.valueAutoAssignMapper(expr);
  }
  return null;
}

/**
 * Determines whether the fallback text should be hidden in the conditional preview for simple conditionals.
 *
 * Hide fallback text if:
 * 1. Both output and fallback are plain values (not bindings)
 * 2. Output value is not equal to the fallback value
 */
export function shouldHideSimpleConditionalPreviewFallbackText(
  type: Type | null,
  outputValue: Expression,
  fallbackValue: Expression
): boolean {
  const conf = getSupportedTypeConfiguration(type);
  if (conf && conf.simpleConditional) {
    return conf.simpleConditional.hidePreviewFallbackValue(
      outputValue,
      fallbackValue
    );
  }
  return false;
}

const builtinBooleanType = qualify(
  Plugins.name('Builtin'),
  typeName('Boolean')
);

export function shouldKeepMiniSettingsOpen(type: Type | null): boolean {
  if (!type) {
    return false;
  }

  const conf = getSupportedTypeConfiguration(type);
  if (conf != null) {
    return conf.keepMiniSettingsOpen ?? false;
  }

  // Special case for the VisibilityConditions type when the alias has been
  // unwrapped to Builtin::List<Builtin::Boolean>
  // We need this because we are targeting the Alias VisibilityConditions in the
  // supported types.
  // Delete this code when we migrate from VisibilityConditions -> Boolean!
  const listItemType = Types.getListItemType(type);
  const con = listItemType ? Types.getTypeConstructor(listItemType) : null;
  return Boolean(con && namesEqual(con, builtinBooleanType));
}

export const getMatchingCallToVariable = (
  expr: Expression,
  expectedVariable: Variable.VariableType
): null | {arg: Expression; fun: Expression} => {
  const call = Expressions.getCall(expr);
  if (!call) {
    return null;
  }
  const variable = Expressions.getVariable(call.fun);
  if (!variable) {
    return null;
  }
  if (!Variable.equals(variable)(expectedVariable)) {
    return null;
  }
  return call;
};

const applyPredicateFirstArg: ExpressionPath = [
  {in: 'Call', at: 'fun'},
  {in: 'Call', at: 'arg'},
];
const applyPredicateNestedFirstArg: ExpressionPath = [
  {in: 'Call', at: 'fun'},
  {in: 'Call', at: 'arg'},
  {in: 'Call', at: 'arg'},
];
// A Condition at the end is
// Conditions::["Apply predicate"]
//   (Boolean::Condition(data.bool))
//   (Boolean::Equals(PrivilegedLab::villain))
//
// We want to remove the full condition if the prop is found in the
// conditional source, so in this example its the arg of Boolean::Condition.
//
// Consistently the path ends
// [..., {in: 'Call', at: 'fun'}, {in: 'Call', at: 'arg'}, {in: 'Call', at: 'arg'}]
// where the "fun" is defining this function
// Conditions::["Apply predicate"]((Boolean::Condition(data.bool)))
//
// So we want to find that expression
export const replaceApplyPredicateWithHole = (
  expr: Expression,
  path: ExpressionPath
): Expression | null => {
  let pathToConditionSource = false;
  let sliceIndex = 0;
  // The path can lead to the argument of the Operand within Apply Predicate or
  // the actual Operand itself. Check for both and remember which one it is.
  if (Expressions.pathsEqual(path.slice(-2), applyPredicateFirstArg)) {
    pathToConditionSource = true;
    sliceIndex = -2;
  } else if (
    Expressions.pathsEqual(path.slice(-3), applyPredicateNestedFirstArg)
  ) {
    pathToConditionSource = true;
    sliceIndex = -3;
  }
  if (pathToConditionSource) {
    // Remove the last arg(s) from the path to find the Apply Predicate,
    // if we find that then we know we want to remove this list entry
    const fnPath = path.slice(0, sliceIndex + 1);
    const fnExpr = Expressions.getIn(expr, fnPath);
    const isCallToApplyPredicate = fnExpr
      ? getMatchingCallToVariable(
          fnExpr,
          Variable.qualified(
            qualify(Plugins.name('Conditions'), identifier('Apply predicate'))
          )
        )
      : null;
    if (isCallToApplyPredicate) {
      const pathToListIndex = path.slice(0, sliceIndex);
      return Expressions.setIn(expr, pathToListIndex, EHole());
    }
  }
  return null;
};

const FALLBACK_PATH: ExpressionPath = [
  {in: 'Call', at: 'arg'},
  {in: 'Record', at: 'fallback'},
];
const RULES_PATH: ExpressionPath = [
  {in: 'Call', at: 'arg'},
  {in: 'Record', at: 'rules'},
];
const CONDITIONS_LIST_PATH: ExpressionPath = [
  {in: 'Record', at: 'condition'},
  {in: 'Call', at: 'arg'},
];

export type TaggedUnlinkedConditional = {
  tag: 'Conditional' | 'Fallback';
  val: Expression;
};

// This util checks a Condition to see if we should remove some conditions,
// remove some groups, or remove the whole condition
export const trimGroupsAndConditions = (
  expr: Expression
): TaggedUnlinkedConditional => {
  // For all groups (rules)
  const rules = Expressions.getIn(expr, RULES_PATH);
  const rulesList = rules ? Expressions.getList(rules) : null;
  if (!rulesList) {
    return {tag: 'Conditional', val: expr};
  }

  const newRulesList = rulesList.filter((rule) => {
    // Condition is a Call to either Every or Some, for the purpose of this util
    // it does not matter. Just read through
    const conditions = Expressions.getIn(rule, CONDITIONS_LIST_PATH);
    const conditionsList = conditions ? Expressions.getList(conditions) : null;
    if (!conditionsList) {
      return false;
    }
    return conditionsList.length > 0;
  });
  // If no groups left, remove the condition and fall back
  // to the fallback value
  if (newRulesList.length === 0) {
    const fallbackExpr = Expressions.getIn(expr, FALLBACK_PATH);
    if (!fallbackExpr) {
      return {tag: 'Conditional', val: expr};
    }
    return {tag: 'Fallback', val: fallbackExpr};
  }
  const newRulesExpr = EList(newRulesList);
  return {
    tag: 'Conditional',
    val: Expressions.setIn(expr, RULES_PATH, newRulesExpr),
  };
};

export const removeHolesFromLists = matchExpression({
  List: (list) => {
    const newList = list.filter((item) => !Expressions.isHole(item));
    return EList(newList);
  },
  _: identity,
});

// Returns the prefix of `path` (including the empty prefix) at which `expr`
// resolves to a Conditional, or null if none of the prefixes do.
export const getConditionalAncestorPath = (
  expr: Expression,
  path: ExpressionPath
): ExpressionPath | null => {
  for (let i = 0; i <= path.length; i++) {
    const prefix = path.slice(0, i);
    const exprAtPath = Expressions.getIn(expr, prefix);
    if (exprAtPath && isConditional(exprAtPath)) {
      return prefix;
    }
  }
  return null;
};

export const maybeRemoveBindingFromConditionalSource = (
  expr: Expression,
  path: ExpressionPath
): Expression | null => {
  // Find and replace an Apply Predicate call with a Hole if our path leads to
  // within a Conditional and a rule conditions source field
  for (let i = 1; i <= path.length; i++) {
    const pathToCheck = path.slice(0, i);
    const exprAtPath = Expressions.getIn(expr, pathToCheck);

    if (exprAtPath && isConditional(exprAtPath)) {
      const pathAfterConditional = path.slice(i);
      // If our path is within a Conditional, check if the path ends in the source field
      // of a Conditions::Apply Predicate structure.
      const conditionalWithMaybeHole = replaceApplyPredicateWithHole(
        exprAtPath,
        pathAfterConditional
      );
      if (conditionalWithMaybeHole) {
        const noHoles = Expressions.everywhere(removeHolesFromLists)(
          conditionalWithMaybeHole
        );

        const trimmedArg = trimGroupsAndConditions(noHoles);
        if (trimmedArg.tag === 'Conditional') {
          // replace the old conditional with the new one
          return Expressions.setIn(expr, pathToCheck, trimmedArg.val);
        } else if (trimmedArg.tag === 'Fallback') {
          // We are removing the conditional and replacing it with a fallback.
          // The conditional might have a wrapping implicit booleanToVisibilityConditions.
          // If so we need to evaluate with it. DYNAMO_BINDING_REMOVED is as of now never
          // called for anything other than Components. If this does get called with
          // element data while booleanToVisibilityConditions still exists we need
          // to add handling here

          // replace the old conditional with the new one
          return Expressions.setIn(expr, pathToCheck, trimmedArg.val);
        }
      }
      // We don't need to check further up the path as conditionals can't be
      // nested
      break;
    }
  }
  return null;
};

const APPLY_PREDICATE_NAME = Variable.qualified(
  qualify(Plugins.name('Conditions'), identifier('Apply predicate'))
);

const expressionContainsHole = Expressions.some(Expressions.isHole);

function isApplyPredicateCondition(expr: Expression): boolean {
  const deconstructed = Expressions.deconstructCall(expr);
  const funVariable = Expressions.getVariable(deconstructed.fun);
  if (!funVariable) {
    return false;
  }
  return Variable.equals(APPLY_PREDICATE_NAME)(funVariable);
}

function conditionHasHole(condition: Expression): boolean {
  const deconstructed = Expressions.deconstructCall(condition);
  const [operandArg, predicateArg] = deconstructed.args;

  if (!operandArg || Expressions.isHole(operandArg)) {
    return true;
  }

  if (predicateArg && expressionContainsHole(predicateArg)) {
    return true;
  }

  return false;
}

const removeHolesAndIncompleteConditions = matchExpression({
  List: (list) => {
    const newList = list.filter((item) => {
      if (Expressions.isHole(item)) {
        return false;
      }
      if (isApplyPredicateCondition(item) && conditionHasHole(item)) {
        return false;
      }
      return true;
    });
    return EList(newList);
  },
  _: identity,
});

export function getCleanedConditionalDispatchValue(
  config: Expression
): TaggedUnlinkedConditional {
  const noHoles = Expressions.everywhere(removeHolesAndIncompleteConditions)(
    config
  );
  return trimGroupsAndConditions(
    ECall({
      fun: EVariable(CONDITIONAL_NAME),
      arg: noHoles,
    })
  );
}

/**
 * Determines whether a new conditional expression can be created at the given
 * expression path, based on feature flags, type support, and available operands.
 */
export function canCreateConditional({
  type,
  rootExpr,
  expressionPath,
  plugins,
  checkerState,
}: {
  type: Type;
  rootExpr: ContextualExpression | null;
  expressionPath: ExpressionPath;
  plugins: Plugins.PluginsType;
  checkerState: CheckerState;
}): boolean {
  return (
    supportsConditionals(type) &&
    !pathContainsConditional(rootExpr, expressionPath) &&
    hasAvailableOperands(plugins)(checkerState)
  );
}
